import {
  type ComputedRef,
  nextTick,
  onBeforeUnmount,
  onMounted,
  type ShallowRef,
  shallowRef,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { setLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { idleWatcher } from '@/services/IdleWatcher';
import { session } from '@/services/Session';
import { captureKioskEvent } from '@/services/telemetry/kiosk-analytics';
import { sentryService } from '@/services/telemetry/sentry';
import { track } from '@/shared/utils/track-telemetry';
import type { HomeSplashVisibility } from './useHomeSplashscreen';

interface IdleWatcherPause {
  continue: () => void;
}

interface UseHomeSessionStartOptions {
  mountedAt: ShallowRef<number | null>;
  showSplashScreen: ComputedRef<HomeSplashVisibility>;
}

interface UseHomeSessionStartResult {
  isCreatingSession: ShallowRef<boolean>;
  onStartButtonClick: () => Promise<void>;
}

export function useHomeSessionStart({
  mountedAt,
  showSplashScreen,
}: UseHomeSessionStartOptions): UseHomeSessionStartResult {
  const route = useRoute();
  const vueRouter = useRouter();
  const isCreatingSession = shallowRef(false);

  let idleWatcherPause: IdleWatcherPause | null = null;
  let isUnmounted = false;

  async function onStartButtonClick(): Promise<void> {
    if (isCreatingSession.value || isUnmounted) {
      return;
    }

    const timeToStart = mountedAt.value ? Date.now() - mountedAt.value : null;
    const fromSplashScreen = Boolean(showSplashScreen.value);
    track('[Sentry] Breadcrumb failed:', () => {
      sentryService.addBreadcrumb('ui.click', 'Start button clicked', {
        time_since_mount_ms: timeToStart,
        from_splash_screen: showSplashScreen.value,
      });
    });

    if (timeToStart !== null) {
      track('[Sentry] Metric tracking failed:', () => {
        sentryService.trackMetric(
          'kiosk.home.time_to_start',
          timeToStart,
          'millisecond',
          {
            has_splash_video: Boolean(store.kiosk?.splashscreen_video_url),
          }
        );
      });
    }

    try {
      isCreatingSession.value = true;

      // withSpan guarantees that the business callback executes exactly once.
      await sentryService.withSpan(
        'kiosk.session.create',
        'session.init',
        async () => {
          await session.create({
            from_splash: fromSplashScreen,
            time_since_mount_ms: timeToStart,
          });
        },
        {
          'kiosk.slug': store.kiosk?.slug,
          'kiosk.gophoto': Boolean(store.kiosk?.gophoto_printing),
        }
      );

      if (isUnmounted) {
        return;
      }

      track('[Sentry] Breadcrumb failed:', () => {
        sentryService.addBreadcrumb('session', 'Session started successfully', {
          session_id: session.id,
        });
      });

      const nextStep = store.kiosk?.gophoto_printing
        ? 'gophoto-scan'
        : 'art-style';

      track('[Sentry] Counter increment failed:', () => {
        sentryService.incrementCounter('kiosk.sessions.started', 1, {
          next_step: nextStep,
        });
      });
      captureKioskEvent('session_started', {
        from_splash: fromSplashScreen,
        next_step: nextStep,
        time_since_mount_ms: timeToStart,
      });

      // Replacing an active session resets i18n during create().
      const localeCode = router.localeCode;
      if (localeCode) {
        setLocale(localeCode);
      }
      await vueRouter.push(router.getLinkToStep(nextStep));
    } catch (error) {
      if (isUnmounted) {
        return;
      }

      // biome-ignore lint/suspicious/noConsole: preserve the operational session failure log.
      console.error('Failed to start session:', error);
      track('[Sentry] captureException failed:', () => {
        sentryService.captureException(error as Error, {
          tags: {
            action: 'session_start',
            component: 'Home',
          },
          extra: {
            kiosk_slug: store.kiosk?.slug,
            time_to_start_ms: timeToStart,
            had_splash_screen: showSplashScreen.value,
          },
          level: 'error',
        });
      });
    } finally {
      isCreatingSession.value = false;
    }
  }

  onMounted(() => {
    const mountStart = performance.now();
    idleWatcherPause = idleWatcher.pause();
    mountedAt.value = Date.now();

    track('[Sentry] Breadcrumb failed:', () => {
      sentryService.addBreadcrumb('ui.lifecycle', 'Home screen mounted', {
        kiosk_slug: store.kiosk?.slug,
        has_splash_video: Boolean(store.kiosk?.splashscreen_video_url),
        viewport: store.viewportIsPortrait ? 'portrait' : 'landscape',
      });
    });
    track('[Sentry] setCurrentStep failed:', () => {
      sentryService.setCurrentStep('home');
    });

    void nextTick().then(() => {
      if (isUnmounted) {
        return;
      }
      const mountDuration = performance.now() - mountStart;
      try {
        sentryService.trackMetric(
          'component.mount_time_ms',
          mountDuration,
          'millisecond',
          {
            component: 'Home',
            route: route.name || 'home',
          }
        );
      } catch (_error) {
        // Mount telemetry must not add console noise or affect the screen.
      }
    });
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
    idleWatcherPause?.continue();
    idleWatcherPause = null;
  });

  return {
    isCreatingSession,
    onStartButtonClick,
  };
}
